import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';

export interface SimpleDataTableColumn<T> {
  key: string;
  header: string;
  width?: string;
  className?: string;
  render: (item: T, index: number) => React.ReactNode;
  /** Optional: Make this column editable. If provided, render will be ignored for editing. */
  editable?: boolean;
  /** Optional: Custom render for editable cell. Receives value, not full item. */
  renderEditable?: (value: unknown, rowIndex: number) => React.ReactNode;
}

export interface SimpleDataTableProps<T> {
  data: T[];
  columns: SimpleDataTableColumn<T>[];
  getRowKey: (item: T, index: number) => string;
  className?: string;
  containerClassName?: string;
  /** Optional: Enable editing mode. When true, editable columns will show input fields. */
  editable?: boolean;
  /** Optional: Callback when cell value changes. */
  onCellChange?: (
    rowIndex: number,
    columnKey: string,
    newValue: unknown,
  ) => void;
}

export function SimpleDataTable<T>({
  data,
  columns,
  getRowKey,
  className,
  containerClassName,
  editable = false,
  onCellChange,
}: SimpleDataTableProps<T>) {
  const handleCellChange = (
    rowIndex: number,
    columnKey: string,
    newValue: string,
  ) => {
    if (onCellChange) {
      onCellChange(rowIndex, columnKey, newValue);
    }
  };

  const renderCell = (
    column: SimpleDataTableColumn<T>,
    item: T,
    rowIndex: number,
  ) => {
    // If editable mode is enabled and this column is editable
    if (editable && column.editable) {
      const value = (item as Record<string, unknown>)[column.key];

      // Use custom editable render if provided
      if (column.renderEditable) {
        return column.renderEditable(value, rowIndex);
      }

      // Default editable render: simple input
      return (
        <Input
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) =>
            handleCellChange(rowIndex, column.key, e.target.value)
          }
          className="min-w-[100px]"
        />
      );
    }

    // Default: use the render function
    return column.render(item, rowIndex);
  };

  return (
    <div className={containerClassName}>
      <Table className={className}>
        <TableHeader>
          <TableRow>
            {columns.map((column) => (
              <TableHead
                key={column.key}
                className={`font-semibold ${column.width || ''} ${
                  column.className || ''
                }`}
              >
                {column.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item, index) => (
            <TableRow key={getRowKey(item, index)}>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={`${column.width || ''} ${column.className || ''}`}
                >
                  {renderCell(column, item, index)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default SimpleDataTable;
